import { useEffect, useRef, useState } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Clock } from "lucide-react";

/**
 * A 24-hour time picker with two scrollable columns (hour / minute),
 * matching the classic "wheel" time picker layout. The trigger and popover
 * both display 24-hour time (e.g. "23:00"); the underlying `value`/`onChange`
 * stay in 24-hour "HH:MM" format for the rest of the app.
 */

const HOURS_24 = Array.from({ length: 24 }, (_, i) => i); // 0..23
const MINUTES = Array.from({ length: 60 }, (_, i) => i); // 0..59

const ROW_HEIGHT = 36;
const VISIBLE_ROWS = 6;

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

/** Is there any trailing digit that, appended to `next`, stays within [min, max]? */
function canExtend(next: string, min: number, max: number) {
  for (let d = 0; d <= 9; d++) {
    const n = parseInt(next + d, 10);
    if (n >= min && n <= max) return true;
  }
  return false;
}

/**
 * A single numeric segment (hour or minute) that only accepts digit keys.
 * Typing builds up a 1-2 digit buffer; once no further digit could keep the
 * value in range (or 2 digits are in), it commits and advances focus.
 */
function useDigitSegment({
  min,
  max,
  value,
  onCommit,
  onAdvance,
}: {
  min: number;
  max: number;
  value: number;
  onCommit: (n: number) => void;
  onAdvance?: () => void;
}) {
  const [text, setText] = useState(pad2(value));
  const bufferRef = useRef("");

  useEffect(() => {
    setText(pad2(value));
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab" || e.key === "Shift") return;
    if (e.key === "Backspace") {
      bufferRef.current = "";
      setText("");
      e.preventDefault();
      return;
    }
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      return;
    }
    e.preventDefault();

    let next = bufferRef.current + e.key;
    let num = parseInt(next, 10);
    if (next.length > 2 || num > max) {
      next = e.key;
      num = parseInt(next, 10);
    }
    bufferRef.current = next;
    setText(next);

    if (num >= min && num <= max) onCommit(num);

    if (next.length === 2 || !canExtend(next, min, max)) {
      bufferRef.current = "";
      onAdvance?.();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    bufferRef.current = "";
    e.target.select();
  };

  const handleBlur = () => {
    bufferRef.current = "";
    setText(pad2(value));
  };

  return { text, handleKeyDown, handleFocus, handleBlur };
}

const SEGMENT_INPUT_CLASS =
  "w-5 shrink-0 bg-transparent text-center tabular-nums outline-none rounded focus:bg-indigo-100 dark:focus:bg-indigo-500/20 cursor-default";

function TimeColumn<T extends string | number>({
  values,
  selected,
  format,
  onSelect,
}: {
  values: readonly T[];
  selected: T;
  format: (v: T) => string;
  onSelect: (v: T) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const idx = values.indexOf(selected);
    if (idx >= 0) el.scrollTop = idx * ROW_HEIGHT;
  }, [selected, values]);

  return (
    <div
      ref={listRef}
      className="overflow-y-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      style={{ height: ROW_HEIGHT * VISIBLE_ROWS, width: 72 }}
    >
      {values.map((v, i) => {
        const active = v === selected;
        return (
          <button
            key={String(v)}
            type="button"
            onClick={() => onSelect(v)}
            style={{ height: ROW_HEIGHT }}
            className={`flex w-full items-center justify-center text-sm tabular-nums transition-colors ${
              active
                ? "font-bold text-white"
                : `font-medium text-[#0f172a] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] ${
                    i % 2 === 1 ? "bg-[#f8fafc] dark:bg-white/[0.02]" : ""
                  }`
            }`}
          >
            <span
              className={
                active ? "flex h-7 w-11 items-center justify-center rounded-lg bg-indigo-600" : undefined
              }
            >
              {format(v)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function ClockTimeInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [hh24, mm] = value ? value.split(":").map(Number) : [9, 0];

  const minuteRef = useRef<HTMLInputElement>(null);

  const setHour = (h: number) => onChange(`${pad2(h)}:${pad2(mm)}`);
  const setMinute = (m: number) => onChange(`${pad2(hh24)}:${pad2(m)}`);

  const hourSegment = useDigitSegment({
    min: 0,
    max: 23,
    value: hh24,
    onCommit: setHour,
    onAdvance: () => minuteRef.current?.focus(),
  });
  const minuteSegment = useDigitSegment({
    min: 0,
    max: 59,
    value: mm,
    onCommit: setMinute,
  });

  return (
    <div className="w-full flex items-center gap-2 text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 border border-[#e2e8f0] dark:border-white/[0.07] focus-within:border-indigo-500 dark:focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/10 transition-all">
      <div className="flex-1 flex items-center text-[#0f172a] dark:text-white">
        <input
          type="text"
          inputMode="numeric"
          aria-label="Hour"
          value={hourSegment.text}
          onChange={() => {}}
          onKeyDown={hourSegment.handleKeyDown}
          onFocus={hourSegment.handleFocus}
          onBlur={hourSegment.handleBlur}
          className={SEGMENT_INPUT_CLASS}
        />
        <span className="flex items-center justify-center w-2 text-[#0f172a] dark:text-white">:</span>
        <input
          ref={minuteRef}
          type="text"
          inputMode="numeric"
          aria-label="Minute"
          value={minuteSegment.text}
          onChange={() => {}}
          onKeyDown={minuteSegment.handleKeyDown}
          onFocus={minuteSegment.handleFocus}
          onBlur={minuteSegment.handleBlur}
          className={SEGMENT_INPUT_CLASS}
        />
      </div>

      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            aria-label="Open time picker"
            className="flex-shrink-0 rounded-md p-0.5 text-[#64748b] dark:text-white/40 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
          >
            <Clock className="size-4" />
          </button>
        </PopoverPrimitive.Trigger>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="end"
            sideOffset={8}
            className="z-[200] rounded-xl border border-border bg-popover text-popover-foreground shadow-xl overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          >
            <div className="flex divide-x divide-border">
              <TimeColumn values={HOURS_24} selected={hh24} format={(v) => pad2(v)} onSelect={setHour} />
              <TimeColumn values={MINUTES} selected={mm} format={(v) => pad2(v)} onSelect={setMinute} />
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </div>
  );
}
